import { Key, ReactNode } from 'react';
import { LatLng } from 'leaflet';
import { PiCarFill } from 'react-icons/pi';
import { Button, Image } from '@ds';
import { AircraftIcon, ClinicalSpecialistUrl } from '@icons';
import { TableColumnType } from '@ui';
import { RouteHub } from '@lp/features/RoutePlanner/types';
import { CaseFacility, OrganType } from '@/api/apis/case';
import { convertMsToTimeString, getColorsByOrganType, getOrganImg } from '@/utils/helpers';
import { EmployeeRole } from '@/api/apis/resources';
import { calculateFlightDurationMs } from '@/utils/route';

export const ROUTE_PLANNER_HUB_TABLE_COLUMNS: TableColumnType[] = [
  {
    key: 'name',
    label: 'Name',
    isSortable: false,
    minWidthClassName: 'max-w-[140px]',
  },
  {
    key: 'resources',
    label: 'Resources',
    isSortable: false,
    minWidthClassName: 'max-w-[220px]',
  },
  {
    key: 'airTime',
    label: <AircraftIcon height={16} width={16} />,
    isSortable: false,
    minWidthClassName: 'min-w-[100px]',
  },
  {
    key: 'carTime',
    label: <PiCarFill className="w-4 h-4" />,
    isSortable: false,
    minWidthClassName: 'min-w-[100px]',
  },
  {
    key: 'actions',
    label: '',
    isSortable: false,
    minWidthClassName: 'min-w-[90px]',
  },
];

export const getRenderRoutePlannerHubTableCell =
  (donorHospital: CaseFacility, onSelect: (hub: RouteHub) => void, organType?: OrganType) =>
  ({ hub, travelTime }: { hub: RouteHub; travelTime?: number }, columnKey: Key): ReactNode => {
    const { name, location, code } = hub;
    const isKidneyCase = organType === OrganType.Kidney;
    const colorsHeart = getColorsByOrganType(OrganType.Heart);
    const colorsLiver = getColorsByOrganType(OrganType.Liver);
    const colorsLungs = getColorsByOrganType(OrganType.Lungs);
    const colorsKidney = getColorsByOrganType(OrganType.Kidney);

    const airTimeInMs = calculateFlightDurationMs(
      new LatLng(location.latitude, location.longitude),
      new LatLng(donorHospital.location.latitude, donorHospital.location.longitude),
    );

    const groundTimeInMs = travelTime || null;

    const onHubSelect = () => onSelect(hub);

    switch (columnKey) {
      case 'name':
        return <span className="text-m-m">{`${name} (${code})`}</span>;
      case 'resources':
        return (
          <div className="flex flex-row items-center">
            <div className={`${colorsHeart.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
              <Image className="w-5 h-5" src={getOrganImg(OrganType.Heart)} alt={`${OrganType.Heart} image`} />
            </div>
            <p className="text-l-r pl-1 pr-3">{hub.resources[OrganType.Heart]}</p>
            <div className={`${colorsLiver.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
              <Image className="w-5 h-5" src={getOrganImg(OrganType.Liver)} alt={`${OrganType.Liver} image`} />
            </div>
            <p className="text-l-r pl-1 pr-3">{hub.resources[OrganType.Liver]}</p>
            <div className={`${colorsLungs.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
              <Image className="w-5 h-5" src={getOrganImg(OrganType.Lungs)} alt={`${OrganType.Lungs} image`} />
            </div>
            <p className="text-l-r pl-1 pr-3">{hub.resources[OrganType.Lungs]}</p>
            {isKidneyCase && (
              <>
                <div className={`${colorsKidney.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                  <Image className="w-5 h-5" src={getOrganImg(OrganType.Kidney)} alt={`${OrganType.Kidney} image`} />
                </div>
                <p className="text-l-r pl-1 pr-3">{hub.resources[OrganType.Kidney]}</p>
              </>
            )}
            <div className="bg-base-40 w-6 h-6 rounded-full flex justify-center items-center">
              <Image className="w-4 h-4" src={ClinicalSpecialistUrl} alt="Clinical Specialist image" />
            </div>
            <p className="text-l-r pl-1 pr-3">{hub.resources[EmployeeRole.ClinicalSpecialist]}</p>
          </div>
        );
      case 'airTime':
        return <span className="text-m-r">{convertMsToTimeString(airTimeInMs)}</span>;
      case 'carTime':
        return (
          <span className="text-m-r">
            {groundTimeInMs && groundTimeInMs > 1 ? convertMsToTimeString(groundTimeInMs) : '-'}
          </span>
        );
      case 'actions':
        return (
          <Button className="w-[69px] h-[30px] " color="primary" variant="outline" onClick={onHubSelect}>
            Select
          </Button>
        );
      default:
        return null;
    }
  };
